<template>
  <div class="report">
    <home clas="bottom-padded-2x"></home>
    <foreword class="bottom-padded-2x"></foreword>
    <ListIndicators class="bottom-padded-2x"></ListIndicators>
    <TableContents class="bottom-padded-2x"></TableContents>
    <button id="go-to-top" v-on:click="goTop"><i class="fa fa-chevron-up" aria-hidden="true"></i> Table of Contents</button>
    <button id="print" v-on:click="print"><i class="fa fa-print" aria-hidden="true"></i> Print</button>
    <Acknowledgements class="bottom-padded-2x"></Acknowledgements>
    <StopProgram class="bottom-padded-2x"></StopProgram>
    <h1 id="1">HIV Testing Episodes and Rates</h1>
    <p>
        In this section, the number of HIV test episodes and point of care (POC) HIV tests
        conducted each quarter in BC is shown. In general terms the goal is to increase the
        number of tests performed and to maximize testing efficiency. Test episodes are
        allocated by region according to where the test is performed.
    </p>
    <p style="font-style: italic;" class="bottom-padded">
      N.B. All HIV Testing Episodes reflect non-prenatal tests. All prenatal tests have been removed.
    </p>
    <h2>Indicator 1. HIV Testing Episodes</h2>
    <Chart figure="1.1" class="bottom-padded"></Chart>
    <Chart figure="1.2"></Chart>
    <Footnotes v-bind:footnotes="[1, 2]" class="bottom-padded"></Footnotes>
    <Chart figure="1.3"></Chart>
    <Footnotes v-bind:footnotes="[1, 2]"  class="bottom-padded"></Footnotes>
    <Chart figure="1.4" class="bottom-padded"></Chart>
    <Chart figure="1.5"></Chart>
    <Footnotes v-bind:footnotes="[1]" class="bottom-padded"></Footnotes>
    <Chart figure="1.6"></Chart>
    <Footnotes v-bind:footnotes="[1]" class="bottom-padded"></Footnotes>
    <Chart figure="1.7"></Chart>
    <Footnotes v-bind:footnotes="[1]" class="bottom-padded-2x"></Footnotes>
    <h2 id="2">Indicator 2. HIV Testing Rates</h2>
    <p style="font-style: italic;">
      N.B. All HIV Testing Episodes reflect non-prenatal tests. All prenatal tests have been removed.
    </p>
    <Chart figure="2.1"></Chart>
    <Footnotes v-bind:footnotes="[2]" class="bottom-padded"></Footnotes>
    <Chart figure="2.2"></Chart>
    <Footnotes v-bind:footnotes="[2]" class="bottom-padded"></Footnotes>
    <Chart figure="2.3"></Chart>
    <Footnotes v-bind:footnotes="[2]" class="bottom-padded-2x"></Footnotes>
    <h1 id="3">New HIV Diagnoses</h1>
    <p class="bottom-padded">
        Trends in HIV diagnoses by gender and exposure category are described. Interpreting
        HIV diagnoses must be done with consideration that trends are influenced by both
        changes in testing rate as well as changes in transmission rates. It is important to
        note that new HIV diagnoses cases and rates are not synonymous with HIV incidence
        as a person may have become infected with HIV long before they tested positive for
        HIV. However, as there is no reliable method for measuring HIV incidence, we follow
        trends in HIV diagnoses.
    </p>
    <h2>Indicator 3. New HIV Diagnoses</h2>
    <Chart figure="3.1"></Chart>
    <Footnotes v-bind:footnotes="[3]" class="bottom-padded"></Footnotes>
    <Chart figure="3.2"></Chart>
    <Footnotes v-bind:footnotes="[3]" class="bottom-padded"></Footnotes>
    <Chart figure="3.3"></Chart>
    <Footnotes v-bind:footnotes="[3]" class="bottom-padded"></Footnotes>
    <Chart figure="3.4"></Chart>
    <Footnotes v-bind:footnotes="[3, 4]" class="bottom-padded"></Footnotes>
    <Chart figure="3.5"></Chart>
    <Footnotes v-bind:footnotes="[3]" class="bottom-padded-2x"></Footnotes>
    <h1 id="4">Stage of HIV Infection at Diagnosis</h1>
    <p>
        Classification of stage of HIV infection, in the absence of
        information regarding recent testing history, is reliant
        on clinical information available at the time of diagnosis,
        including first CD4+ cell count and laboratory results
        suggestive of acute HIV infection (Table 1). The benefits
        of Treatment as Prevention (TasP) are maximized when
        antiretroviral therapy (ART) is initiated at high CD4 cell
        counts. Accordingly, it is preferable that individuals newly
        diagnosed with HIV be in the early stages of HIV infection
        (stage 0 or 1) to allow for early ART initiation.
    </p>
    <p style="font-style: italic;" class="bottom-padded">
      N.B. Interpretation of Stage of HIV Infection at Diagnosis should proceed
      with caution. Early increases in diagnosis at late stage (i.e., low CD4
      counts) may represent a "catching up" of previously missed long term
      infected individuals rather than a trend toward diagnosis at later stage
      of infection. Additionally, there has been an increase in the proportion
      of new diagnosis in 2016 for whom these data are missing (37%),  leading
      to a slight overestimation of the overall proportion within each stage.
    </p>
    <h2>Indicator 4. Stage of HIV Infection at Diagnosis</h2>
    <h3 id="table1">Table 1: Staging Classifications of Infection at Time of HIV Diagnosis Based on CDC HIV Surveillance Case Definitions</h3>
    <Table1 class="bottom-padded"></Table1>
    <div class="inline-box bottom-padded">
      <Chart figure="4.1"></Chart>
      <Chart figure="4.2" class="left-padded"></Chart>
    </div>
    <DataTable v-bind:figures="[4.1, 4.2]"></DataTable>
    <Footnotes v-bind:footnotes="[5]" class="bottom-padded"></Footnotes>
    <Chart figure="4.3" class="bottom-padded"></Chart>
    <Chart figure="4.4" class="bottom-padded"></Chart>
    <DataTable v-bind:figures="[4.3, 4.4]"></DataTable>
    <Footnotes v-bind:footnotes="[5, 6]" class="bottom-padded-2x"></Footnotes>
    <h2 id="5">Indicator 5. HIV Cascade of Care</h2>
    <p>
        The success of seek, test, treat and retain (STTR) strategies like STOP is reliant on early diagnosis of HIV, linking newly
        diagnosed HIV-positive persons with ongoing care, retaining persons in HIV-care; initiating ART based on best evidenced
        practices and maintaining optimal ART adherence to ensure a suppressed viral load. These stages of HIV-care
        can be summarized as: 1. HIV diagnosis, 2. Linked to HIV care, 3. Retained in HIV care, 4. On ART, 5. Adherent to ART
        and 6. Achieving a suppressed VL; collectively, they are referred to as the cascade of care. Attrition between any of
        these stages of HIV-care means a reduction in the potential of ART as a benefit to the HIV-positive individual and as an
        HIV transmission prevention method on a population level. Thus, when interpreting trends in the cascade of care, we
        strive to see increases along each step of the cascade of care (i.e. reduced attrition) with the ultimate goal being 100%
        within each stage of the cascade. Monitoring the Cascade of Care provides a picture as to where deficiencies lie in the
        delivery and uptake of HIV-care. In this section, we present the cascade of care for the period stated in BC overall and
        stratified by sex and age for each Health Authority.
    </p>
    <div class="inline-box">
      <Chart figure="5.1"></Chart>
      <Chart figure="5.2"></Chart>
    </div>
    <Footnotes v-bind:footnotes="[7]" class="bottom-padded"></Footnotes>
    <Chart figure="5.3"></Chart>
    <Footnotes v-bind:footnotes="[8]" class="bottom-padded"></Footnotes>
    <Chart figure="5.4"></Chart>
    <Footnotes v-bind:footnotes="[9]" class="bottom-padded"></Footnotes>
    <Chart figure="5.5"></Chart>
    <Footnotes v-bind:footnotes="[9]" class="bottom-padded"></Footnotes>
    <Chart figure="5.6"></Chart>
    <Footnotes v-bind:footnotes="[9]" class="bottom-padded"></Footnotes>
    <Chart figure="5.7"></Chart>
    <Footnotes v-bind:footnotes="[9]" class="bottom-padded-2x"></Footnotes>
    <h2 id="6">Indicator 6. Programmatic Compliance Score (PCS)</h2>
    <p>
        The Programmatic Compliance Score (PCS) is a summary measure of risk of future
        death, immunologic failure and virologic failure from all causes for people who are
        starting ART for the first time. It is composed of patient- and physician-driven effects.
        PCS scores range from 0–6 with higher scores indicative of poorer health outcomes
        and greater risk of death. Table 2 provides mortality, immunologic failure and virologic
        failure probabilities for given PCS scores. We interpret an individual with a PCS≥4 as
        being 22 times more likely to die, almost 10 times more likely to have immunologic
        failure and nearly 4 times as likely to demonstrate virologic failure compared to
        those individuals with a PCS score of 0. A detailed description of how the PCS score
        is calculated and its validation can be found in the technical report. In short, PCS
        scores are calculated by summing the results (yes=1, no=0) of six un-weighted nonperformance
        indicators based on IAS–USA treatment guidelines:
    </p>
    <ol>
        <li>having <span style="color: #0076a3"><3 CD4 cell count tests</span> in the first year after starting antiretroviral therapy (ART);</li>
        <li>having <span style="color: #9d0140"><3 plasma viral load (VL) tests</span> in the first year after starting ART; </li>
        <li><span style="color: #ab9f01">not having drug resistance testing</span> done prior to starting ART;</li>
        <li>starting on a <span style="color: #560054">non-recommended ART</span> regimen;</li>
        <li>starting therapy with <span style="color: #007236">CD4<200 cells/µL</span>; and</li>
        <li><span style="color: #9d070d">not achieving viral suppression</span> within 9 months since ART initiation. </li>
    </ol>
    <p class="bottom-padded">
        In this section we provide PCS scores and their components over time for the province
        of BC. A decline to 0%, <b>(i.e., all individuals having a score of 0)</b> is the eventual goal.
    </p>
    <h3 id="table2">Table 2. Probability of Mortality, Immunologic Failure and Virologic Failure based on the Programmatic Compliance Score</h3>
    <DataTable v-bind:figures="[6]"></DataTable>
    <p class="bottom-padded">
        <i>Reference: Lima VD, Le A, Nosyk B, Barrios R, Yip B, et al. (2012) Development and Validation
        of a Composite Programmatic Assessment Tool for HIV Therapy. PLoS ONE 7(11): e47859.
        doi:10.1371/journal.pone.0047859</i>
    </p>
    <Chart figure="6.1"></Chart>
    <Footnotes v-bind:footnotes="[10]" class="bottom-padded"></Footnotes>
    <Chart figure="6.2"></Chart>
    <Footnotes v-bind:footnotes="[10, 11]" class="bottom-padded-2x"></Footnotes>
    <h1 id="7a">Indicator 7. New Antiretroviral Therapy Starts in {{haName}}</h1>
    <h2>Antiretroviral Uptake</h2>
    <p>
        In this section we present trends in ART uptake, the number and proportion of new HIV treatment initiations
        and the number of active and inactive DTP participants. Trends in ART uptake should be interpreted under the
        consideration of changing BC HIV treatment guidelines. BC HIV treatment guidelines are updated regularly by
        the BC-CfE Therapeutic Guidelines Committee and reflect those of the International AIDS Society. Since 2012 HIV
        treatment has been recommended for all HIV-positive adults regardless of CD4 cell count; as evidence demonstrates
        that early initiation of HIV treatment maximizes both the individual’s health outcomes as well as the potential
        of ART as a form of HIV transmission prevention at a population level.
        As such, trends in the number and proportion of persons on ART and new ART starts (in both naïve and experienced
        persons) are expected to increase over time at higher CD4 cell counts.
    </p>
    <Chart figure="7"></Chart>
    <Footnotes v-bind:footnotes="[12]" class="bottom-padded"></Footnotes>
    <h2 id="8a">Indicator 8. CD4 Cell Count at ART Initiation</h2>
    <Chart figure="8"></Chart>
    <Footnotes v-bind:footnotes="[13]" class="bottom-padded"></Footnotes>
    <h2 id="9a">Indicator 9. Active and Inactive DTP Participants</h2>
    <h3 id="table3">Table 3. Distribution of People on ART for {{table3.lastYear}} Q{{table3.lastQuarter}} <sup>14</sup></h3>
    <DataTable v-bind:figures="[9]"></DataTable>
    <Footnotes v-bind:footnotes="[14]" class="bottom-padded"></Footnotes>
    <div>
      <Chart figure="9"></Chart>
      <div class="breaker" v-bind:style="breakStyle">
        <div class="container3 zigzag top"></div>
        <div class="container3 zigzag bottom"></div>
      </div>
    </div>
    <Footnotes v-bind:footnotes="[15]" class="bottom-padded-2x"></Footnotes>
    <h1 id="10a">Indicator 10. Antiretroviral Adherence Level</h1>
    <!-- <h2>Antiretroviral Adherence Level</h2> -->
    <p>
        In this section we present trends in prescription refill adherence levels for
        individuals in their first year of treatment. Given that the benefits of ART are
        compromised in the presence of imperfect ART adherence, we expect to see the
        proportion of persons on ART achieving <span style="color: #007596;">near perfect adherence</span> (ie. <span style="color: #007596;">≥95%</span>) to
        increase with time. Furthermore, it is important that trends in the proportion
        of ART users achieving prescription refill adherence of <span style="color: #007596;">≥95%</span> keep pace with
        new ART starts and increase among those continuing on ART.
    </p>
    <Chart figure="10"></Chart>
    <Footnotes v-bind:footnotes="[16]" class="bottom-padded-2x"></Footnotes>
    <h1 id="11a">Resistance Testing and Results</h1>
    <h2>Indicator 11. Resistance Testing and Results</h2>
    <p>
        In this section, we present trends in cumulative resistance testing by resistance category:
        <span style="color: #560054">Suppressed</span> (where a DTP participant’s viral load is too low to be genotyped); <span style="color: #0076a3">Wild
        Type</span> (where no HIV treatment resistances were discovered), <span style="color: #086c28">Never Genotyped</span>, and
        Resistances to <span style="color: #ab9f01">one</span>, <span style="color: #a36109">two</span>, <span style="color: #9d070d">three</span>, or <span style="color: #4f0e0e">four</span> HIV treatment classes. Resistance testing prior to
        ART initiation is recommended in the BC HIV treatment primary care guidelines. Thus,
        it is expected that trends over time should find all persons enrolled in the DTP to have
        been genotyped. Trends over time should also show an increase in the proportion of DTP
        participants achieving a suppressed status and an increase in resistance testing should
        not lead to an increase in the number of ART resistances occurring.
    </p>
    <Chart figure="11"></Chart>
    <Footnotes v-bind:footnotes="[17]" class="bottom-padded-2x"></Footnotes>
    <h1 id="12a">AIDS-Defining Illness</h1>
    <h2>Indicator 12. AIDS-Defining Illness</h2>
    <p>
        Improvements in ART and the expansion of ART province-wide has led to very low numbers of recorded AIDS
        cases across BC. However, interpreting trends in AIDS cases is challenging as AIDS reporting is passive in BC and
        it is likely that they are under-reported across all Health Authorities. In addition to under-reporting, methods of
        reporting AIDS cases are inconsistent across HA’s and do not truly reflect the current reality of new AIDS diagnoses.
        Efforts will need to be made to improve under- and inconsistent reporting of AIDS cases across all HA’s. The
        table below shows AIDS cases using three definitions. First, AIDS cases were defined as the number of physician reported
        AIDS defining illness (ADI) in a given year. AIDS case reporting is a passive process and physicians
        can voluntarily report AIDS cases to the BCCDC or DTP. As such, we have plotted both <span style="color: #ab9f01">BCCDC reports</span> and <span style="color: #9d0140">DTP
            reported AIDS cases</span>. We also show the proportion of persons <span style="color: #0076a3">initiating ART with a CD4<200 cells/µL</span>.
    </p>
    <Chart figure="12"></Chart>
    <Footnotes v-bind:footnotes="[18]" class="bottom-padded-2x"></Footnotes>
    <h1 id="13a">HIV-Related Mortality</h1>
    <h2 id="13">Indicator 13. HIV-Related Mortality for all BC</h2>
    <p>
        Evidence indicates that individuals who initiate treatment with
        recommended ART in a timely fashion may live near normal lifespans.
        Excess mortality among HIV positive persons is, therefore, an
        important measure of HIV care with a goal of minimizing HIV-related
        mortality in British Columbia.
    </p>
    <div>
      <Chart figure="13"></Chart>
      <!-- <div style="height: 32px;position: relative;top: -305px;background-color: white;left: 150px;width: 868px;"></div> -->
    </div>
    <Footnotes v-bind:footnotes="[19]" class="bottom-padded-2x"></Footnotes>
    <Appendices></Appendices>
  </div>
</template>

<script>
  import * as $ from 'jquery';
  import Home from '@/components/Home'
  import Foreword from '@/components/Foreword'
  import ListIndicators from '@/components/ListIndicators'
  import TableContents from '@/components/TableContents'
  import Acknowledgements from '@/components/Acknowledgements'
  import StopProgram from '@/components/StopProgram'
  import Table1 from '@/components/Table1'
  import Chart from '@/components/Chart'
  import Footnotes from '@/components/Footnotes'
  import DataTable from '@/components/DataTable'
  import Appendices from '@/components/Appendices'

  const settings = require('./../../../static/report.json');

  export default {
    components: {
      Home,
      Foreword,
      ListIndicators,
      TableContents,
      Acknowledgements,
      StopProgram,
      Table1,
      Chart,
      Footnotes,
      DataTable,
      Appendices
    },
    methods: {
      goTop: function (event) {
        // document.location.href = '#toc';
        $('html,body').animate({scrollTop: $("#toc").offset().top}, 'fast');
      },
      print: function (event) {
        ga('send', 'event', 'PDF Report', 'download', this.$route.params.ha);
        window.open(`./static/report_${this.$route.params.ha}.pdf`, '_blank');
      }
    },
    props: {
      ha: {
        default: 'bc',
        type: String,
      },
    },
    computed: {
      haName() {
        switch (this.$route.params.ha) {
          case 'bc':
            return 'BC'
            break;
          case 'fha':
            return 'Fraser Health';
            break;
          case  'iha':
            return 'Interior Health';
            break;
          case 'nha':
            return 'Northern Health';
            break;
          case 'vcha':
            return 'Vancouver Coastal Health';
            break;
          case 'viha':
            return 'Vancouver Island Health';
            break;
          default:
            return 'Ukn'
        }
      },
      period() {
        return '2013 Q3-2018 Q2'
      },
      groupBy() {
        return this.$route.params.ha === 'bc' ? 'Health Authority' : 'HSDA'
      },
      groupByPlural() {
        return this.$route.params.ha === 'bc' ? 'Health Authorities' : 'HSDAs'
      },
      lastYear() {
        return this.period.slice(8, -3); //2017
      },
      lastQuarter() {
        return this.period.slice(-1); //4
      }
    },
    data () {
      const table3 = require(`./../../../temp/json/figure_${this.$route.params.ha}_9.json`).report;
      return {
        table3,
        breakStyle: {
          position: 'relative',
          top: settings.breaks[this.$route.params.ha][0].position + 'px',
          left: '80px'
        }
      }
    }
  }
</script>

<style>
  @media print {
    .report {
      max-width: 1200px;
    }
    button{
      display: none !important;
    }
  }

  @media screen {
    .report {
      max-width: 1200px;
    }
    button{
      display: block;
    }
  }

  .highcharts-tooltip div {
    z-index:9999 !important;
  }

  .report {
    margin: 0 auto;
    border: 1px solid #d6dfe6;
    background-color: #fff;
    border-radius: 1px;
    box-sizing: border-box;
    overflow: hidden;
    padding: 10px;
  }

  button#go-to-top {
    display: none;
    position: fixed;
    bottom: 30px;
    right: 90px;
    z-index: 99;
    height: 32px;
    font-family: 'Lato', sans-serif;
  }

  button#print {
    display: block;
    position: fixed;
    bottom: 30px;
    right: 30px;
    z-index: 99;
    height: 32px;
    font-family: 'Lato', sans-serif;
  }

  .zigzag {
    position: relative;
    width: 100%;
    height: 4px;
  }
  .zigzag:before {
    content: "";
    display: block;
    position: absolute;
    top: -10px;
    width: 100%;
    height: 10px;
  }
  .zigzag.top {
    position: relative;
    left: 8px;
  }
  .zigzag.bottom {
    -webkit-transform: rotate(180deg);
  }
  .container3 {
    background: #FFF;
  }
  .container3:before {
    background:
      linear-gradient(
        45deg, transparent 33.333%,
        #FFF 33.333%, #FFF 66.667%,
        transparent 66.667%
      ),
      linear-gradient(
        -45deg, transparent 33.333%,
        #FFF 33.333%, #FFF 66.667%,
        transparent 66.667%
      );
    background-size: 20px 40px;
  }

  h1 {
    display: block;
    font-size: 2em;
    -webkit-margin-before: 0.67em;
    -webkit-margin-after: 0.67em;
    -webkit-margin-start: 0px;
    -webkit-margin-end: 0px;
    font-weight: bold;
  }

  h2 {
    display: block;
    font-size: 1.5em;
    -webkit-margin-before: 0.83em;
    -webkit-margin-after: 0.83em;
    -webkit-margin-start: 0px;
    -webkit-margin-end: 0px;
    font-weight: bold;
  }

  h3 {
    display: block;
    font-size: 1.17em;
    -webkit-margin-before: 1em;
    -webkit-margin-after: 1em;
    -webkit-margin-start: 0px;
    -webkit-margin-end: 0px;
    font-weight: bold;
  }
</style>



// WEBPACK FOOTER //
// Report.vue?44a7f402